import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import axios from 'axios';  // Use raw axios for external API calls
import axiosAuth from './axiosInstance';  // Use for internal authenticated API calls
import TokenManager from './TokenManager';
import './styles/RFIDBatchAssign.css';
import { AuthContext } from './context/AuthContext';
import ProtectedComponent from './ProtectedComponent';

const API_BASE_URL = "https://eastus.common.solumesl.com/rfidbe/api/v1/rfid/devices";
const API_DETAILS_URL = "https://eastus.common.solumesl.com/rfidbe/api/v1/rfid/devices/details";
const COMPANY = "WLAB";
const STORE = "100";

const RFIDBatchAssign = () => {
  const { user } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('assign');
  const [accessToken, setAccessToken] = useState(sessionStorage.getItem('SaaS_accessToken') || '');

  // API Configuration - use SaaS token from TokenManager
  const apiKey = accessToken;
  const [company, setCompany] = useState(COMPANY);
  const [store, setStore] = useState(STORE);

  // Assignment parameters
  const [deviceIds, setDeviceIds] = useState('');
  const [rackId, setRackId] = useState('');
  const [group, setGroup] = useState('A');

  // Status and history
  const [statusLog, setStatusLog] = useState([]);
  const [history, setHistory] = useState([]);
  const [devices, setDevices] = useState([]);
  const [devicesColumns, setDevicesColumns] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [editingCell, setEditingCell] = useState(null);
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'readerCode', direction: 'asc' });
  const [filterText, setFilterText] = useState('');
  const [hasLoadedDevices, setHasLoadedDevices] = useState(false);
  const [pendingUpdates, setPendingUpdates] = useState({});
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [isAssignmentLocked, setIsAssignmentLocked] = useState(false);
  const [conflictModal, setConflictModal] = useState(null);

  // Rack reassignment state
  const [showRackReassignmentModal, setShowRackReassignmentModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [foundDevices, setFoundDevices] = useState([]);
  const [newRackId, setNewRackId] = useState('');
  const [newGroup, setNewGroup] = useState('A');
  const [isSearching, setIsSearching] = useState(false);
  const [reassignmentLog, setReassignmentLog] = useState([]);
  const [searchType, setSearchType] = useState(''); // 'device' or 'rack'

  // History pagination state
  const [historyPage, setHistoryPage] = useState(0);
  const [historyPageSize] = useState(50);
  const [historyFilterText, setHistoryFilterText] = useState('');

  // Refs for auto-focus
  const batchDeviceIdsRef = useRef(null);
  const singleScanInputRef = useRef(null);

  // Single Scan state
  const [scannedDeviceId, setScannedDeviceId] = useState('');
  const [scannedDeviceData, setScannedDeviceData] = useState(null);
  const [isLoadingScannedDevice, setIsLoadingScannedDevice] = useState(false);
  const [editingScannedField, setEditingScannedField] = useState(null);
  const [pendingScannedChange, setPendingScannedChange] = useState(null);

  // Ground Truth state
  const [gtCurrentSession, setGtCurrentSession] = useState(null);
  const [gtRackId, setGtRackId] = useState('');
  const [gtManualUpcs, setGtManualUpcs] = useState([]);
  const [gtManualInput, setGtManualInput] = useState('');
  const [gtZebraResults, setGtZebraResults] = useState([]);
  const [gtComparisonResult, setGtComparisonResult] = useState(null);
  const [gtSessions, setGtSessions] = useState([]);
  const [gtIsLoading, setGtIsLoading] = useState(false);
  const [gtView, setGtView] = useState('new'); // 'new', 'history', 'stats'

  // Auto-search when 12 characters are entered in Single Scan
  useEffect(() => {
    if (activeTab === 'scan' && scannedDeviceId.length === 12 && apiKey) {
      loadScannedDevice();
    }
  }, [scannedDeviceId, activeTab, apiKey]);

  useEffect(() => {
    loadHistory();
  }, []);

  useEffect(() => {
    // Auto-load devices the first time user switches to "All Devices" tab
    if (activeTab === 'devices' && !hasLoadedDevices && apiKey) {
      loadAllDevices();
    }
  }, [activeTab, hasLoadedDevices, apiKey]);

  const logStatus = (message) => {
    const timestamp = new Date().toLocaleTimeString();
    setStatusLog(prev => [...prev, `[${timestamp}] ${message}`]);
  };

  // Check for duplicate device IDs in the batch assign textarea
  const duplicateDeviceIds = useMemo(() => {
    const lines = deviceIds.trim().split('\n').map(id => id.trim()).filter(Boolean);
    const seen = new Map();
    const duplicates = new Set();

    lines.forEach((id, index) => {
      if (seen.has(id)) {
        duplicates.add(id);
      } else {
        seen.set(id, index);
      }
    });

    return duplicates;
  }, [deviceIds]);

  // Validate that there are no duplicate indexes in the assign list for the same shelf
  const validateNoDuplicateIndexes = (assignList, existingDevices) => {
    // Group by shelf ID
    const byShelf = {};

    // Add existing devices (excluding ones being reassigned)
    const reassignedReaderCodes = new Set(assignList.map(a => a.readerCode));
    existingDevices.forEach(device => {
      if (!reassignedReaderCodes.has(device.readerCode) && device.shelfId) {
        if (!byShelf[device.shelfId]) byShelf[device.shelfId] = new Set();
        byShelf[device.shelfId].add(parseInt(device.index) || 0);
      }
    });

    // Check new assignments
    assignList.forEach(assignment => {
      const shelfId = assignment.shelfId;
      const index = parseInt(assignment.index) || 0;

      if (!byShelf[shelfId]) byShelf[shelfId] = new Set();

      if (byShelf[shelfId].has(index)) {
        throw new Error(`Duplicate index ${index} detected on rack "${shelfId}". This would create a conflict.`);
      }

      byShelf[shelfId].add(index);
    });

    return true;
  };

  const loadHistory = async () => {
    try {
      const response = await fetch('/api/rfid/history', {
        credentials: 'include'  // Send authentication cookies
      });
      if (response.ok) {
        const data = await response.json();
        setHistory(data);
      } else if (response.status === 404) {
        console.log('History endpoint not available');
      }
    } catch (error) {
      console.log('Failed to load history:', error.message);
    }
  };

  const loadLeaderboard = async () => {
    try {
      const response = await fetch('/api/rfid/leaderboard', {
        credentials: 'include'  // Send authentication cookies
      });
      const data = await response.json();
      setLeaderboardData(data);
      setShowLeaderboard(true);
    } catch (error) {
      console.error('Failed to load leaderboard:', error);
      alert('Failed to load leaderboard');
    }
  };

  const saveToHistory = async (assignList, eventType = 'assign') => {
    const assignments = assignList.map(item => ({
      deviceId: item.readerCode,
      rackId: item.shelfId,
      index: item.index,
      group: item.groupName,
      eventType: eventType
    }));

    // Get username from AuthContext
    const saasUsername = user?.displayName || user?.firstName || user?.email || 'Unknown';

    try {
      const response = await fetch('/api/rfid/history', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assignments, assignedBy: saasUsername })
      });

      if (response.ok) {
        logStatus(`Saved ${assignList.length} ${eventType}s to history`);
        loadHistory(); // Reload history from server
      } else {
        logStatus(`Failed to save history: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Failed to save history:', error);
      logStatus(`Error saving history: ${error.message}`);
    }
  };

  const handleConflictResolution = async (action) => {
    if (!conflictModal) return;

    const { alreadyAssigned, targetRackId, deviceIdList, allDevices } = conflictModal;
    setIsAssignmentLocked(true);

    if (action === 'cancel') {
      setConflictModal(null);
      setIsAssignmentLocked(false);
      logStatus(`✗ Assignment cancelled by user`);
      return;
    }

    if (action === 'reassign') {
      // Option 1: Reassign only the conflicting devices
      logStatus(`Unassigning ${alreadyAssigned.length} conflicting device(s)...`);

      try {
        const readerCodes = alreadyAssigned.map(d => d.deviceId);
        const unassignUrl = `${API_BASE_URL}?company=${company}&store=${store}`;
        const unassignHeaders = {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        };
        const unassignBody = { readerList: readerCodes };

        const unassignResponse = await axios.delete(unassignUrl, { headers: unassignHeaders, data: unassignBody });

        if ([200, 204].includes(unassignResponse.status)) {
          logStatus(`✓ Successfully unassigned ${alreadyAssigned.length} device(s)`);

          // Track unassigns in history
          const unassignList = alreadyAssigned.map(device => ({
            readerCode: device.deviceId,
            shelfId: device.rackId,
            index: device.index,
            groupName: device.groupName
          }));
          saveToHistory(unassignList, 'unassign');

          // Continue with assignment
          setConflictModal(null);
          await continueWithAssignment(deviceIdList, targetRackId, allDevices);
        } else {
          setIsAssignmentLocked(false);
          const errorMsg = unassignResponse.data?.responseMessage || unassignResponse.data;
          alert(`Failed to unassign devices: ${unassignResponse.status}\n${errorMsg}`);
          logStatus(`✗ ERROR: Failed to unassign devices`);
        }
      } catch (error) {
        setIsAssignmentLocked(false);
        alert(`Failed to unassign devices: ${error.message}`);
        logStatus(`✗ ERROR: ${error.message}`);
      }
    } else if (action === 'clear_rack') {
      // Option 2: Clear entire rack first
      const devicesOnRack = allDevices.filter(d => d.shelfId === targetRackId);

      if (devicesOnRack.length === 0) {
        logStatus(`No devices found on rack "${targetRackId}" to clear`);
        setConflictModal(null);
        await continueWithAssignment(deviceIdList, targetRackId, allDevices);
        return;
      }

      logStatus(`Clearing all ${devicesOnRack.length} device(s) from rack "${targetRackId}"...`);

      try {
        const readerCodes = devicesOnRack.map(d => d.readerCode);
        const unassignUrl = `${API_BASE_URL}?company=${company}&store=${store}`;
        const unassignHeaders = {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        };
        const unassignBody = { readerList: readerCodes };

        const unassignResponse = await axios.delete(unassignUrl, { headers: unassignHeaders, data: unassignBody });

        if ([200, 204].includes(unassignResponse.status)) {
          logStatus(`✓ Successfully cleared ${devicesOnRack.length} device(s) from rack "${targetRackId}"`);

          // Track unassigns in history
          const unassignList = devicesOnRack.map(device => ({
            readerCode: device.readerCode,
            shelfId: device.shelfId,
            index: device.index,
            groupName: device.groupName || ''
          }));
          saveToHistory(unassignList, 'unassign');

          // Continue with assignment starting at index 1
          setConflictModal(null);
          await continueWithAssignment(deviceIdList, targetRackId, allDevices, 1);
        } else {
          setIsAssignmentLocked(false);
          const errorMsg = unassignResponse.data?.responseMessage || unassignResponse.data;
          alert(`Failed to clear rack: ${unassignResponse.status}\n${errorMsg}`);
          logStatus(`✗ ERROR: Failed to clear rack`);
        }
      } catch (error) {
        setIsAssignmentLocked(false);
        alert(`Failed to clear rack: ${error.message}`);
        logStatus(`✗ ERROR: ${error.message}`);
      }
    }
  };

  const continueWithAssignment = async (deviceIdList, targetRackId, allDevices, forceStartIndex = null) => {
    // Determine starting index
    let startingIndex = forceStartIndex || 1;

    if (forceStartIndex === null) {
      const devicesOnRack = allDevices.filter(d => d.shelfId === targetRackId);
      if (devicesOnRack.length > 0) {
        const indexes = devicesOnRack.map(d => parseInt(d.index) || 0);
        const maxIndex = Math.max(...indexes);
        startingIndex = maxIndex + 1;
        logStatus(`Found ${devicesOnRack.length} existing devices on rack '${targetRackId}'. Starting at index ${startingIndex}`);
      } else {
        logStatus(`No existing devices found on rack '${targetRackId}'. Starting at index 1`);
      }
    }

    // Build assign list
    const assignList = deviceIdList.map((deviceId, idx) => ({
      readerCode: deviceId.trim(),
      shelfId: targetRackId,
      index: startingIndex + idx,
      groupName: group,
      location_info: {}
    }));

    logStatus(`Preparing to assign ${deviceIdList.length} devices to rack '${targetRackId}', group '${group}' (indexes ${startingIndex}-${startingIndex + deviceIdList.length - 1})`);

    try {
      const url = `${API_BASE_URL}?company=${company}&store=${store}`;
      const headers = {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      };
      const body = { assignList };

      logStatus(`Sending POST request to: ${url}`);
      const response = await axios.post(url, body, { headers });

      logStatus(`Response status: ${response.status}`);

      let success = false;
      if (response.data.responseCode) {
        if (['200', '201', 200, 201].includes(response.data.responseCode)) {
          success = true;
        } else {
          const errorMsg = response.data.responseMessage || 'Unknown error';
          logStatus(`✗ API ERROR: ${response.data.responseCode} - ${errorMsg}`);
          setIsAssignmentLocked(false);
          alert(`Error ${response.data.responseCode}: ${errorMsg}`);
          return;
        }
      } else {
        success = true;
      }

      if (success && [200, 201].includes(response.status)) {
        logStatus('✓ SUCCESS: Batch assignment completed successfully!');
        saveToHistory(assignList);
        alert(`Successfully assigned ${deviceIdList.length} devices!`);
      } else {
        logStatus(`✗ ERROR: ${response.status} - ${response.data}`);
        setIsAssignmentLocked(false);
        alert(`API Error: ${response.status}\n${response.data}`);
      }
    } catch (error) {
      logStatus(`✗ EXCEPTION: ${error.message}`);
      setIsAssignmentLocked(false);
      alert(`Request failed: ${error.message}`);
    }
  };

  const submitBatchAssignment = async () => {
    // Validate API key from session
    if (!apiKey) {
      alert('Please login with your SaaS credentials first');
      return;
    }

    // Log token for debugging
    logStatus(`Using token: ${apiKey.substring(0, 20)}...${apiKey.substring(apiKey.length - 10)}`);

    // Validate device IDs
    const deviceIdList = deviceIds.trim().split('\n').filter(id => id.trim());
    if (deviceIdList.length === 0) {
      alert('Please enter at least one Device ID');
      return;
    }
    if (deviceIdList.length > 20) {
      alert('Maximum 20 Device IDs allowed');
      return;
    }

    // Validate rack ID
    if (!rackId.trim()) {
      alert('Please enter a Rack ID');
      return;
    }

    // Lock the form now that submission is starting
    setIsAssignmentLocked(true);

    // Check for existing devices on this rack to determine starting index
    logStatus(`Checking for existing devices on rack '${rackId}'...`);
    let startingIndex = 1;

    try {
      const headers = {
        'Authorization': `Bearer ${apiKey}`,
        'Accept': 'application/json'
      };

      // Load all devices and filter by rack ID
      let allDevices = [];
      let page = 0;
      const PAGE_SIZE = 500;

      while (true) {
        const url = `${API_BASE_URL}?company=${company}&store=${store}&page=${page}&size=${PAGE_SIZE}`;
        const response = await axios.get(url, { headers });

        let deviceData = response.data;
        if (!Array.isArray(deviceData)) {
          deviceData = response.data?.data || response.data?.responseMessage?.data || [];
        }

        if (!Array.isArray(deviceData) || deviceData.length === 0) break;

        allDevices = [...allDevices, ...deviceData];

        if (deviceData.length < PAGE_SIZE) break;
        page += 1;
      }

      // Check if any of the device IDs are already assigned
      const alreadyAssigned = [];
      deviceIdList.forEach(deviceId => {
        const trimmedId = deviceId.trim();
        const existingDevice = allDevices.find(d => d.readerCode === trimmedId);
        if (existingDevice && existingDevice.shelfId) {
          alreadyAssigned.push({
            deviceId: trimmedId,
            rackId: existingDevice.shelfId,
            index: existingDevice.index,
            groupName: existingDevice.groupName || ''
          });
        }
      });

      if (alreadyAssigned.length > 0) {
        // Show modal and wait for user choice
        setConflictModal({
          alreadyAssigned,
          targetRackId: rackId.trim(),
          deviceIdList,
          allDevices,
          startingIndex
        });
        setIsAssignmentLocked(false);
        return;
      }

      // Filter devices by matching rack ID
      const rackIdToMatch = rackId.trim();
      logStatus(`Looking for devices with shelfId matching: "${rackIdToMatch}"`);
      logStatus(`Total devices loaded: ${allDevices.length}`);

      // Debug: show some sample shelfIds
      const sampleShelfIds = allDevices.slice(0, 5).map(d => d.shelfId);
      logStatus(`Sample shelfIds from API: ${JSON.stringify(sampleShelfIds)}`);

      const devicesOnRack = allDevices.filter(d => d.shelfId === rackIdToMatch);

      if (devicesOnRack.length > 0) {
        // Find highest index
        const indexes = devicesOnRack.map(d => parseInt(d.index) || 0);
        logStatus(`Existing indexes on rack: ${JSON.stringify(indexes)}`);
        const maxIndex = Math.max(...indexes);
        startingIndex = maxIndex + 1;
        logStatus(`Found ${devicesOnRack.length} existing devices on rack '${rackIdToMatch}'. Highest index: ${maxIndex}`);
        logStatus(`Starting new assignments at index ${startingIndex}`);
      } else {
        logStatus(`No existing devices found on rack '${rackIdToMatch}'. Starting at index 1`);
      }
    } catch (error) {
      logStatus(`Warning: Could not check existing devices: ${error.message}`);
      logStatus(`Defaulting to starting index 1`);
    }

    // Build assign list with calculated starting index
    const assignList = deviceIdList.map((deviceId, idx) => ({
      readerCode: deviceId.trim(),
      shelfId: rackId.trim(),
      index: startingIndex + idx,
      groupName: group,
      location_info: {}
    }));

    logStatus(`Preparing to assign ${deviceIdList.length} devices to rack '${rackId}', group '${group}' (indexes ${startingIndex}-${startingIndex + deviceIdList.length - 1})`);

    try {
      const url = `${API_BASE_URL}?company=${company}&store=${store}`;
      const headers = {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      };
      const body = { assignList };

      logStatus(`Sending POST request to: ${url}`);
      logStatus(`Request body: ${JSON.stringify(body, null, 2)}`);

      const response = await axios.post(url, body, { headers });

      logStatus(`Response status: ${response.status}`);
      logStatus(`Response JSON: ${JSON.stringify(response.data, null, 2)}`);

      // Check response
      let success = false;
      if (response.data.responseCode) {
        if (['200', '201', 200, 201].includes(response.data.responseCode)) {
          success = true;
        } else {
          const errorMsg = response.data.responseMessage || 'Unknown error';
          logStatus(`✗ API ERROR: ${response.data.responseCode} - ${errorMsg}`);
          setIsAssignmentLocked(false);
          alert(`Error ${response.data.responseCode}: ${errorMsg}`);
          return;
        }
      } else {
        success = true;
      }

      if (success && [200, 201].includes(response.status)) {
        logStatus('✓ SUCCESS: Batch assignment completed successfully!');
        saveToHistory(assignList);
        alert(`Successfully assigned ${deviceIdList.length} devices!`);
      } else {
        logStatus(`✗ ERROR: ${response.status} - ${response.data}`);
        setIsAssignmentLocked(false);
        alert(`API Error: ${response.status}\n${response.data}`);
      }
    } catch (error) {
      logStatus(`✗ EXCEPTION: ${error.message}`);
      setIsAssignmentLocked(false);
      alert(`Request failed: ${error.message}`);
    }
  };

  const loadAllDevices = async () => {
    if (!apiKey) {
      alert('Please login with your SaaS credentials first');
      return;
    }

    setIsLoadingDevices(true);

    try {
      let allDevices = [];
      let page = 0;
      const PAGE_SIZE = 500;

      console.log('Starting device load...');

      while (true) {
        const url = `${API_BASE_URL}?company=${company}&store=${store}&page=${page}&size=${PAGE_SIZE}`;
        const headers = {
          'Authorization': `Bearer ${apiKey}`,
          'Accept': 'application/json'
        };

        const response = await axios.get(url, { headers });
        console.log(`Page ${page} response:`, response.data);

        // The response is directly an array
        let deviceData = response.data;

        // If it's wrapped in an object, try to extract it
        if (!Array.isArray(deviceData)) {
          deviceData = response.data?.data || response.data?.responseMessage?.data || [];
        }

        if (!Array.isArray(deviceData)) {
          console.log('Not an array, response:', response.data);
          break;
        }

        console.log(`Page ${page}: got ${deviceData.length} devices`);
        if (deviceData.length === 0) break;

        allDevices = [...allDevices, ...deviceData];

        // Continue if we got a full page
        if (deviceData.length < PAGE_SIZE) {
          console.log('Got less than PAGE_SIZE, stopping');
          break;
        }
        page += 1;
      }

      console.log(`Total devices loaded: ${allDevices.length}`);

      if (allDevices.length === 0) {
        alert('No devices found');
        return;
      }

      // Get columns
      const allKeys = new Set();
      allDevices.forEach(device => {
        if (device) Object.keys(device).forEach(key => allKeys.add(key));
      });

      // Put readerCode, shelfId, index, gateway, lastResponseTime, battery, signal, and groupName first, then the rest alphabetically
      const priorityColumns = ['readerCode', 'shelfId', 'index', 'gateway', 'lastResponseTime', 'battery', 'signal', 'groupName'];
      const excludedColumns = ['group_name']; // Exclude group_name since we now use groupName from the result set
      const otherColumns = [...allKeys].filter(k => !priorityColumns.includes(k) && !excludedColumns.includes(k)).sort();
      const columns = [...priorityColumns.filter(k => allKeys.has(k)), ...otherColumns];

      setDevicesColumns(columns);

      console.log('Setting devices state:', allDevices);
      console.log('Setting columns state:', columns);
      setDevices(allDevices);
      setDevicesColumns(columns);
      setHasLoadedDevices(true);
    } catch (error) {
      console.error('Load devices error:', error);
      alert(`Failed to load devices: ${error.message}`);
    } finally {
      setIsLoadingDevices(false);
    }
  };

  const unassignSelectedDevices = async () => {
    if (selectedDevices.length === 0) {
      alert('Please select devices to unassign');
      return;
    }

    if (!apiKey) {
      alert('Please login with your SaaS credentials first');
      return;
    }

    // selectedDevices now contains readerCodes directly
    const readerCodes = selectedDevices.filter(Boolean);

    if (readerCodes.length === 0) {
      alert('No valid reader codes found in selection');
      return;
    }

    if (!window.confirm(`Are you sure you want to unassign ${readerCodes.length} device(s)?`)) {
      return;
    }

    // Capture device info before unassigning for history tracking
    const devicesToUnassign = devices.filter(device => readerCodes.includes(device.readerCode));
    const unassignList = devicesToUnassign.map(device => ({
      readerCode: device.readerCode,
      shelfId: device.shelfId || '',
      index: device.index || 0,
      groupName: device.groupName || ''
    }));

    try {
      const url = `${API_BASE_URL}?company=${company}&store=${store}`;
      const headers = {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      };
      const body = { readerList: readerCodes };

      const response = await axios.delete(url, { headers, data: body });

      if ([200, 204].includes(response.status)) {
        alert(`Successfully unassigned ${readerCodes.length} device(s)`);
        saveToHistory(unassignList, 'unassign');
        setSelectedDevices([]);
        loadAllDevices();
      } else {
        const errorMsg = response.data?.responseMessage || response.data;
        alert(`API Error: ${response.status}\n${errorMsg}`);
      }
    } catch (error) {
      alert(`Failed to unassign devices: ${error.message}`);
    }
  };

  const exportToExcel = () => {
    if (devices.length === 0) {
      alert('No devices to export');
      return;
    }

    const sortedDevices = getSortedDevices();

    // Create CSV content
    let csv = devicesColumns.join(',') + '\n';

    sortedDevices.forEach(device => {
      const row = devicesColumns.map(col => {
        const value = device[col];
        if (value === null || value === undefined) return '';
        if (typeof value === 'object') return `"${JSON.stringify(value).replace(/"/g, '""')}"`;
        const str = String(value);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      });
      csv += row.join(',') + '\n';
    });

    // Download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `rfid_devices_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const updateDeviceAssignment = async (deviceData) => {
    if (!apiKey) {
      alert('Please login with your SaaS credentials first');
      return;
    }

    const readerCode = deviceData.readerCode || '';
    const shelfId = deviceData.shelfId || deviceData.shelfName || '';
    const indexVal = deviceData.index || '1';
    const groupName = deviceData.groupName || '';

    if (!readerCode) {
      alert('Cannot update: missing readerCode');
      return;
    }

    if (!shelfId) {
      alert('Cannot update: missing shelfId');
      return;
    }

    const assignList = [{
      readerCode,
      shelfId,
      index: parseInt(indexVal) || 1,
      groupName,
      location_info: {}
    }];

    try {
      const url = `${API_BASE_URL}?company=${company}&store=${store}`;
      const headers = {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      };
      const body = { assignList };

      const response = await axios.post(url, body, { headers });

      if ([200, 201].includes(response.status)) {
        alert(`Successfully updated assignment for ${readerCode}`);
        saveToHistory(assignList);
        loadAllDevices();
      } else {
        const errorMsg = response.data?.responseMessage || response.data;
        alert(`API Error: ${response.status}\n${errorMsg}`);
      }
    } catch (error) {
      alert(`Failed to update assignment: ${error.message}`);
    }
  };

  const handleCellDoubleClick = (device, colName) => {
    if (colName !== 'index' && colName !== 'groupName') {
      alert("Only the 'index' and 'groupName' columns are editable. Double-click on a value to edit it.");
      return;
    }
    setEditingCell({ device, colName });
  };

  const handleCellEdit = (device, colName, newValue) => {
    // Find the device in the original array by readerCode (unique identifier)
    const deviceIndex = devices.findIndex(d => d.readerCode === device.readerCode);
    if (deviceIndex === -1) {
      alert('Device not found in list');
      return;
    }

    const updatedDevices = [...devices];
    const oldValue = updatedDevices[deviceIndex][colName];
    updatedDevices[deviceIndex][colName] = newValue;
    setDevices(updatedDevices);
    setEditingCell(null);

    // For index column, update immediately as before
    if (colName === 'index') {
      updateDeviceAssignment(updatedDevices[deviceIndex]);
    } else if (colName === 'groupName') {
      // For groupName, add to pending updates
      if (newValue !== oldValue) {
        setPendingUpdates(prev => ({
          ...prev,
          [device.readerCode]: {
            device: updatedDevices[deviceIndex],
            oldValue,
            newValue
          }
        }));
      }
    }
  };

  const toggleDeviceSelection = (readerCode) => {
    setSelectedDevices(prev =>
      prev.includes(readerCode) ? prev.filter(i => i !== readerCode) : [...prev, readerCode]
    );
  };

  const handleBatchUpdate = async () => {
    const updateList = Object.values(pendingUpdates);

    if (updateList.length === 0) {
      alert('No pending updates');
      return;
    }

    // Build assignList for batch update
    const assignList = updateList.map(update => ({
      readerCode: update.device.readerCode,
      shelfId: update.device.shelfId || update.device.shelfName || '',
      index: parseInt(update.device.index) || 1,
      groupName: update.newValue,
      location_info: {}
    }));

    try {
      const url = `${API_BASE_URL}?company=${company}&store=${store}`;
      const headers = {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      };
      const body = { assignList };

      const response = await axios.post(url, body, { headers });

      if ([200, 201].includes(response.status)) {
        alert(`Successfully updated ${assignList.length} device(s)`);
        saveToHistory(assignList, 'group_change');
        setPendingUpdates({});
        setShowUpdateModal(false);
        loadAllDevices();
      } else {
        const errorMsg = response.data?.responseMessage || response.data;
        alert(`API Error: ${response.status}\n${errorMsg}`);
      }
    } catch (error) {
      alert(`Failed to update assignments: ${error.message}`);
    }
  };

  const handleSort = (columnKey) => {
    let direction = 'asc';
    if (sortConfig.key === columnKey && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: columnKey, direction });
  };

  const getSortedDevices = () => {
    // First filter
    let filtered = devices;
    if (filterText.trim()) {
      const searchLower = filterText.toLowerCase();
      filtered = devices.filter(device => {
        return devicesColumns.some(col => {
          const value = device[col];
          if (value == null) return false;
          const valueStr = typeof value === 'object' ? JSON.stringify(value) : String(value);
          return valueStr.toLowerCase().includes(searchLower);
        });
      });
    }

    // Then sort
    if (!sortConfig.key) return filtered;

    const sorted = [...filtered].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      // Handle null/undefined
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      // Handle objects (convert to string)
      const aStr = typeof aVal === 'object' ? JSON.stringify(aVal) : String(aVal);
      const bStr = typeof bVal === 'object' ? JSON.stringify(bVal) : String(bVal);

      // Compare
      if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  const logReassignment = (message) => {
    const timestamp = new Date().toLocaleTimeString();
    setReassignmentLog(prev => [...prev, `[${timestamp}] ${message}`]);
  };

  const searchDevices = async () => {
    if (!searchQuery.trim()) {
      alert('Please enter a Device ID or Rack ID to search');
      return;
    }

    // Check if devices are loaded
    if (devices.length === 0) {
      alert('Please load devices first by clicking "Load All Devices"');
      return;
    }

    setIsSearching(true);
    setFoundDevices([]);
    setReassignmentLog([]);
    const query = searchQuery.trim().toUpperCase();
    logReassignment(`Searching for: ${query}`);
    logReassignment(`Searching in ${devices.length} loaded devices...`);

    try {
      // First, try to find by Device ID (readerCode)
      const deviceById = devices.find(d =>
        d.readerCode && d.readerCode.toUpperCase() === query
      );

      if (deviceById) {
        // Found a device - get all devices on the same rack
        setSearchType('device');
        logReassignment(`✓ Found device: ${deviceById.readerCode}`);
        logReassignment(`  Current Rack ID: ${deviceById.shelfId || 'Not assigned'}`);
        logReassignment(`  Current Index: ${deviceById.index || 'N/A'}`);
        logReassignment(`  Current Group: ${deviceById.groupName || 'N/A'}`);

        if (deviceById.shelfId) {
          const devicesOnRack = devices.filter(d => d.shelfId === deviceById.shelfId);
          logReassignment(`\nFound ${devicesOnRack.length} device(s) on rack "${deviceById.shelfId}":`);
          devicesOnRack.forEach(d => {
            logReassignment(`  - ${d.readerCode} (Index: ${d.index}, Group: ${d.groupName || 'N/A'})`);
          });
          setFoundDevices(devicesOnRack);
        } else {
          logReassignment(`\n⚠ Device is not currently assigned to any rack`);
          setFoundDevices([deviceById]);
        }
      } else {
        // Not found by device ID, try by Rack ID
        const devicesOnRack = devices.filter(d => d.shelfId && d.shelfId.toUpperCase() === query);

        if (devicesOnRack.length > 0) {
          setSearchType('rack');
          logReassignment(`✓ Found ${devicesOnRack.length} device(s) on rack "${query}":`);
          devicesOnRack.forEach(d => {
            logReassignment(`  - ${d.readerCode} (Index: ${d.index}, Group: ${d.groupName || 'N/A'})`);
          });
          setFoundDevices(devicesOnRack);
        } else {
          // Nothing found
          logReassignment(`✗ No devices found matching: ${query}`);
          alert(`No devices found matching "${query}"\n\nTry searching by:\n• Device ID (e.g., 01234567890ABC)\n• Rack ID (e.g., R-100)`);
        }
      }

    } catch (error) {
      logReassignment(`✗ Error: ${error.message}`);
      alert(`Failed to search: ${error.message}`);
    } finally {
      setIsSearching(false);
    }
  };

  const getPaginatedHistory = () => {
    // First filter
    let filtered = history;
    if (historyFilterText.trim()) {
      const searchLower = historyFilterText.toLowerCase();
      filtered = history.filter(entry => {
        return (
          (entry.deviceId && entry.deviceId.toLowerCase().includes(searchLower)) ||
          (entry.rackId && entry.rackId.toLowerCase().includes(searchLower)) ||
          (entry.group && entry.group.toLowerCase().includes(searchLower)) ||
          (entry.eventType && entry.eventType.toLowerCase().includes(searchLower)) ||
          (entry.assignedBy && entry.assignedBy.toLowerCase().includes(searchLower)) ||
          (entry.index && String(entry.index).includes(searchLower))
        );
      });
    }

    // Then paginate
    const startIndex = historyPage * historyPageSize;
    const endIndex = startIndex + historyPageSize;
    return filtered.slice(startIndex, endIndex);
  };

  const getFilteredHistoryCount = () => {
    if (!historyFilterText.trim()) return history.length;
    const searchLower = historyFilterText.toLowerCase();
    return history.filter(entry => {
      return (
        (entry.deviceId && entry.deviceId.toLowerCase().includes(searchLower)) ||
        (entry.rackId && entry.rackId.toLowerCase().includes(searchLower)) ||
        (entry.group && entry.group.toLowerCase().includes(searchLower)) ||
        (entry.eventType && entry.eventType.toLowerCase().includes(searchLower)) ||
        (entry.assignedBy && entry.assignedBy.toLowerCase().includes(searchLower)) ||
        (entry.index && String(entry.index).includes(searchLower))
      );
    }).length;
  };

  const historyTotalPages = Math.ceil(getFilteredHistoryCount() / historyPageSize);

  const loadScannedDevice = async () => {
    if (!scannedDeviceId.trim()) {
      alert('Please enter a Device ID');
      return;
    }

    if (!apiKey) {
      alert('Please login with your SaaS credentials first');
      return;
    }

    setIsLoadingScannedDevice(true);
    setScannedDeviceData(null);

    try {
      const url = `${API_DETAILS_URL}?company=${company}&store=${store}&readerCode=${scannedDeviceId.trim()}`;
      const headers = {
        'Authorization': `Bearer ${apiKey}`,
        'Accept': 'application/json'
      };

      console.log('[single-scan] Fetching device details:', url);
      const response = await axios.get(url, { headers });

      console.log('[single-scan] Response:', response.data);
      setScannedDeviceData(response.data);
    } catch (error) {
      console.error('[single-scan] Error:', error);
      alert(`Failed to load device: ${error.message}`);
    } finally {
      setIsLoadingScannedDevice(false);
    }
  };

  const handleScannedFieldChange = (fieldName, newValue) => {
    // Get old value
    let oldValue;
    if (fieldName === 'shelf_id') {
      oldValue = scannedDeviceData.shelf_id || scannedDeviceData.shelfId;
    } else if (fieldName === 'index_order') {
      oldValue = String(scannedDeviceData.index_order || scannedDeviceData.index || '');
    } else if (fieldName === 'group_name') {
      oldValue = scannedDeviceData.group_name || scannedDeviceData.groupName || '';
    }

    // Check if value actually changed
    if (String(newValue) !== String(oldValue)) {
      setPendingScannedChange({ fieldName, newValue, oldValue });
    } else {
      setPendingScannedChange(null);
    }
    setEditingScannedField(null);
  };

  const saveScannedDeviceChange = async () => {
    if (!pendingScannedChange || !scannedDeviceData) return;

    const { fieldName, newValue } = pendingScannedChange;
    const readerCode = scannedDeviceData.reader_code || scannedDeviceData.readerCode;
    const shelfId = fieldName === 'shelf_id' ? newValue : (scannedDeviceData.shelf_id || scannedDeviceData.shelfId);
    const indexOrder = fieldName === 'index_order' ? parseInt(newValue) || 1 : (scannedDeviceData.index_order || scannedDeviceData.index || 1);
    const groupName = fieldName === 'group_name' ? newValue : (scannedDeviceData.group_name || scannedDeviceData.groupName || '');

    if (!shelfId) {
      alert('Shelf ID is required');
      return;
    }

    const assignList = [{
      readerCode,
      shelfId,
      index: indexOrder,
      groupName,
      location_info: {}
    }];

    try {
      const url = `${API_BASE_URL}?company=${company}&store=${store}`;
      const headers = {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      };
      const body = { assignList };

      const response = await axios.post(url, body, { headers });

      if ([200, 201].includes(response.status)) {
        alert(`Successfully updated ${fieldName}`);
        saveToHistory(assignList);
        setPendingScannedChange(null);
        // Reload the device to show updated data
        loadScannedDevice();
      } else {
        const errorMsg = response.data?.responseMessage || response.data;
        alert(`API Error: ${response.status}\n${errorMsg}`);
      }
    } catch (error) {
      alert(`Failed to update: ${error.message}`);
    }
  };

  const unassignScannedDevice = async () => {
    if (!scannedDeviceData) {
      alert('No device loaded');
      return;
    }

    if (!apiKey) {
      alert('Please login with your SaaS credentials first');
      return;
    }

    const readerCode = scannedDeviceData.reader_code || scannedDeviceData.readerCode;
    const currentRack = scannedDeviceData.shelf_id || scannedDeviceData.shelfId || 'N/A';
    const currentIndex = scannedDeviceData.index_order || scannedDeviceData.index || 'N/A';
    const currentGroup = scannedDeviceData.group_name || scannedDeviceData.groupName || 'N/A';

    if (!window.confirm(`Are you sure you want to unassign this device?\n\nDevice: ${readerCode}\nCurrent Rack: ${currentRack}\nIndex: ${currentIndex}\nGroup: ${currentGroup}`)) {
      return;
    }

    try {
      const url = `${API_BASE_URL}?company=${company}&store=${store}`;
      const headers = {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      };
      const body = { readerList: [readerCode] };

      const response = await axios.delete(url, { headers, data: body });

      if ([200, 204].includes(response.status)) {
        alert(`Successfully unassigned device ${readerCode}`);

        // Save to history if device was assigned
        if (scannedDeviceData.shelf_id || scannedDeviceData.shelfId) {
          const unassignList = [{
            readerCode,
            shelfId: scannedDeviceData.shelf_id || scannedDeviceData.shelfId || '',
            index: scannedDeviceData.index_order || scannedDeviceData.index || 0,
            groupName: scannedDeviceData.group_name || scannedDeviceData.groupName || ''
          }];
          saveToHistory(unassignList, 'unassign');
        }

        // Reload the device to show updated data
        loadScannedDevice();
      } else {
        const errorMsg = response.data?.responseMessage || response.data;
        alert(`API Error: ${response.status}\n${errorMsg}`);
      }
    } catch (error) {
      alert(`Failed to unassign: ${error.message}`);
    }
  };

  const reassignRack = async () => {
    if (foundDevices.length === 0) {
      alert('No devices to reassign');
      return;
    }

    if (!apiKey) {
      alert('Please login with your SaaS credentials first');
      return;
    }

    // Determine the rack ID to use (new rack ID if provided, otherwise current rack ID)
    const currentRack = foundDevices[0]?.shelfId || 'unassigned';
    const targetRackId = newRackId.trim() || currentRack;

    // Check if anything is actually changing
    const currentGroup = foundDevices[0]?.groupName || '';
    const isRackChanging = newRackId.trim() && newRackId.trim() !== currentRack;
    const isGroupChanging = newGroup !== currentGroup;

    if (!isRackChanging && !isGroupChanging) {
      alert('Please change either the Rack ID or Group to perform reassignment');
      return;
    }

    // Build appropriate confirmation message
    let confirmMessage;
    if (isRackChanging && isGroupChanging) {
      confirmMessage = `Are you sure you want to reassign ${foundDevices.length} device(s) from rack "${currentRack}" to rack "${targetRackId}" and change group from "${currentGroup}" to "${newGroup}"?\n\nDevices to reassign:\n${foundDevices.map(d => `  - ${d.readerCode}`).join('\n')}`;
    } else if (isRackChanging) {
      confirmMessage = `Are you sure you want to reassign ${foundDevices.length} device(s) from rack "${currentRack}" to rack "${targetRackId}" (keeping group "${newGroup}")?\n\nDevices to reassign:\n${foundDevices.map(d => `  - ${d.readerCode}`).join('\n')}`;
    } else {
      confirmMessage = `Are you sure you want to change the group for ${foundDevices.length} device(s) on rack "${currentRack}" from "${currentGroup}" to "${newGroup}"?\n\nDevices to update:\n${foundDevices.map(d => `  - ${d.readerCode}`).join('\n')}`;
    }

    if (!window.confirm(confirmMessage)) {
      return;
    }

    logReassignment(`\nStarting reassignment to rack "${targetRackId}" with group "${newGroup}"...`);

    try {
      // Calculate starting index for the target rack if rack is changing
      let startingIndex = 1;

      if (isRackChanging) {
        // When moving to a different rack, we need to find the highest existing index on target rack
        const devicesOnTargetRack = devices.filter(d => d.shelfId === targetRackId);

        if (devicesOnTargetRack.length > 0) {
          const indexes = devicesOnTargetRack.map(d => parseInt(d.index) || 0);
          const maxIndex = Math.max(...indexes);
          startingIndex = maxIndex + 1;
          logReassignment(`Found ${devicesOnTargetRack.length} existing devices on target rack "${targetRackId}". Starting at index ${startingIndex}`);
        } else {
          logReassignment(`Target rack "${targetRackId}" is empty. Starting at index 1`);
        }
      }

      // Build assign list - use targetRackId (which is either newRackId or current rack)
      const assignList = foundDevices.map((device, idx) => ({
        readerCode: device.readerCode,
        shelfId: targetRackId,
        index: isRackChanging ? (startingIndex + idx) : (device.index || 1),
        groupName: newGroup,
        location_info: {}
      }));

      // Validate no duplicate indexes before sending
      try {
        validateNoDuplicateIndexes(assignList, devices);
        logReassignment(`✓ Validation passed: No duplicate indexes detected`);
      } catch (validationError) {
        logReassignment(`✗ VALIDATION ERROR: ${validationError.message}`);
        alert(`Assignment blocked: ${validationError.message}\n\nPlease contact support if this issue persists.`);
        return;
      }

      const url = `${API_BASE_URL}?company=${company}&store=${store}`;
      const headers = {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      };
      const body = { assignList };

      logReassignment(`Sending reassignment request...`);
      const response = await axios.post(url, body, { headers });

      if ([200, 201].includes(response.status)) {
        const successMessage = isRackChanging && isGroupChanging
          ? `${foundDevices.length} device(s) reassigned to rack "${targetRackId}" with group "${newGroup}"`
          : isRackChanging
          ? `${foundDevices.length} device(s) reassigned to rack "${targetRackId}"`
          : `${foundDevices.length} device(s) updated to group "${newGroup}" on rack "${targetRackId}"`;

        logReassignment(`✓ SUCCESS: ${successMessage}`);
        saveToHistory(assignList, isGroupChanging && !isRackChanging ? 'group_change' : 'rack_reassignment');
        alert(`Successfully ${successMessage}!`);

        // Reset form and close modal
        setSearchQuery('');
        setFoundDevices([]);
        setNewRackId('');
        setNewGroup('A');
        setSearchType('');
        setShowRackReassignmentModal(false);

        // Reload devices to show updated data
        loadAllDevices();
      } else {
        const errorMsg = response.data?.responseMessage || response.data;
        logReassignment(`✗ ERROR: ${response.status} - ${errorMsg}`);
        alert(`API Error: ${response.status}\n${errorMsg}`);
      }
    } catch (error) {
      logReassignment(`✗ EXCEPTION: ${error.message}`);
      alert(`Request failed: ${error.message}`);
    }
  };

  return (
    <ProtectedComponent functionCode="SA04" functionName="RFID Batch Assign">
      <div className="rfid-batch-assign">
      {/* TokenManager fetches and maintains SaaS token */}
      <TokenManager onTokenUpdate={setAccessToken} />

      <h1>RFID Management Tool</h1>

      <div className="tabs">
        <button className={activeTab === 'assign' ? 'active' : ''} onClick={() => setActiveTab('assign')}>
          Batch Assign
        </button>
        <button className={activeTab === 'history' ? 'active' : ''} onClick={() => setActiveTab('history')}>
          History
        </button>
        <button className={activeTab === 'devices' ? 'active' : ''} onClick={() => setActiveTab('devices')}>
          All Devices
        </button>
        <button className={activeTab === 'scan' ? 'active' : ''} onClick={() => setActiveTab('scan')}>
          Single Scan
        </button>
        <button className={activeTab === 'groundtruth' ? 'active' : ''} onClick={() => setActiveTab('groundtruth')}>
          Ground Truth
        </button>
      </div>

      {activeTab === 'assign' && (
        <div className="tab-content">
          <div className="section api-config">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <label style={{ fontWeight: '600', margin: 0, minWidth: '70px' }}>Company:</label>
                <input type="text" value={company} onChange={e => setCompany(e.target.value)} style={{ width: '100px', padding: '6px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '14px' }} />
              </div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <label style={{ fontWeight: '600', margin: 0, minWidth: '70px' }}>Store:</label>
                <input type="text" value={store} onChange={e => setStore(e.target.value)} style={{ width: '100px', padding: '6px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '14px' }} />
              </div>
            </div>
          </div>

          <div className="section device-ids">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <p style={{ margin: 0, fontWeight: '600', fontSize: '14px' }}>Enter Device IDs (one per line, max 20):</p>
              <button
                onClick={() => {
                  setDeviceIds('');
                  setIsAssignmentLocked(false);
                  // Focus the textarea after clearing
                  setTimeout(() => {
                    if (batchDeviceIdsRef.current) {
                      batchDeviceIdsRef.current.focus();
                    }
                  }, 0);
                }}
                style={{
                  padding: '6px 10px',
                  fontSize: '12px',
                  whiteSpace: 'nowrap',
                  color: 'white',
                  backgroundColor: '#003366',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Clear
              </button>
            </div>
            <textarea
              ref={batchDeviceIdsRef}
              value={deviceIds}
              onChange={e => setDeviceIds(e.target.value)}
              rows={8}
              disabled={isAssignmentLocked}
              style={{
                backgroundColor: isAssignmentLocked ? '#e0e0e0' : (duplicateDeviceIds.size > 0 ? '#ffe6e6' : 'white'),
                color: isAssignmentLocked ? '#666' : 'black',
                cursor: isAssignmentLocked ? 'not-allowed' : 'text',
                border: duplicateDeviceIds.size > 0 ? '2px solid #dc3545' : '1px solid #ccc'
              }}
            />
            {duplicateDeviceIds.size > 0 && (
              <div style={{
                marginTop: '8px',
                padding: '10px',
                backgroundColor: '#f8d7da',
                border: '1px solid #dc3545',
                borderRadius: '4px',
                color: '#721c24',
                fontSize: '13px'
              }}>
                <strong>⚠ Duplicate Device IDs detected:</strong>
                <div style={{ marginTop: '5px', fontFamily: 'monospace' }}>
                  {Array.from(duplicateDeviceIds).map((id, idx) => (
                    <div key={idx} style={{ color: '#dc3545', fontWeight: 'bold' }}>• {id}</div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="section assignment-params">
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '10px' }}>
              <label style={{ fontWeight: '600', margin: 0 }}>Rack ID:</label>
              <input type="text" value={rackId} onChange={e => setRackId(e.target.value)} style={{ width: '120px', padding: '6px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '14px' }} />
            </div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'nowrap' }}>
              <label style={{ fontWeight: '600', margin: 0 }}>Group:</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', margin: 0, whiteSpace: 'nowrap' }}>
                <input
                  type="radio"
                  name="group"
                  value="A"
                  checked={group === 'A'}
                  onChange={e => setGroup(e.target.value)}
                  style={{ cursor: 'pointer' }}
                />
                <span>Group A</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', margin: 0, whiteSpace: 'nowrap' }}>
                <input
                  type="radio"
                  name="group"
                  value="B"
                  checked={group === 'B'}
                  onChange={e => setGroup(e.target.value)}
                  style={{ cursor: 'pointer' }}
                />
                <span>Group B</span>
              </label>
            </div>
          </div>

          <button
            className="submit-btn"
            onClick={submitBatchAssignment}
            disabled={isAssignmentLocked}
            style={{
              backgroundColor: isAssignmentLocked ? '#cccccc' : undefined,
              color: isAssignmentLocked ? '#666' : undefined,
              cursor: isAssignmentLocked ? 'not-allowed' : 'pointer',
              opacity: isAssignmentLocked ? 0.6 : 1
            }}
          >
            SUBMIT BATCH ASSIGNMENT
          </button>

          <div className="section status">
            <h3>Status</h3>
            <div className="status-log">
              {statusLog.map((log, idx) => (
                <div key={idx}>{log}</div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="tab-content">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '10px' }}>
            <h2 style={{ margin: 0 }}>Assignment History</h2>
            <button onClick={loadLeaderboard} style={{
              padding: '4px 10px',
              backgroundColor: '#ffa500',
              color: 'white',
              border: 'none',
              borderRadius: '3px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '12px',
              whiteSpace: 'nowrap'
            }}>
              🏆 Leaderboard
            </button>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <input
              type="text"
              placeholder="Search history..."
              value={historyFilterText}
              onChange={(e) => {
                setHistoryFilterText(e.target.value);
                setHistoryPage(0); // Reset to first page when searching
              }}
              style={{
                width: '100%',
                maxWidth: '300px',
                padding: '6px 10px',
                border: '2px solid #333',
                borderRadius: '4px',
                fontSize: '13px',
                color: '#000',
                backgroundColor: '#fff'
              }}
            />
          </div>
          <div className="devices-table-wrapper" style={{ position: 'relative' }}>
            <table className="history-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Device ID</th>
                  <th>Rack ID</th>
                  <th>Index</th>
                  <th>Group</th>
                  <th>Event</th>
                  <th>User</th>
                </tr>
              </thead>
              <tbody>
                {getPaginatedHistory().map((entry, idx) => (
                  <tr key={entry._id || idx}>
                    <td>{new Date(entry.timestamp).toLocaleString()}</td>
                    <td>{entry.deviceId}</td>
                    <td>{entry.rackId}</td>
                    <td>{entry.index}</td>
                    <td>{entry.group}</td>
                    <td style={{
                      textTransform: 'capitalize',
                      color: entry.eventType === 'assign' ? '#28a745' : entry.eventType === 'unassign' ? '#dc3545' : '#ffc107'
                    }}>
                      {entry.eventType || 'assign'}
                    </td>
                    <td>{entry.assignedBy || 'Unknown'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{
              position: 'sticky',
              bottom: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              padding: '6px 10px',
              backgroundColor: 'white',
              borderTop: '1px solid #ddd',
              gap: '3px'
            }}>
              <button
                onClick={() => setHistoryPage(historyPage - 1)}
                disabled={historyPage === 0}
                style={{
                  padding: '3px 6px',
                  backgroundColor: historyPage === 0 ? '#ccc' : '#003366',
                  color: 'white',
                  border: 'none',
                  borderRadius: '3px',
                  cursor: historyPage === 0 ? 'not-allowed' : 'pointer',
                  fontSize: '11px',
                  fontWeight: '600',
                  minWidth: '24px'
                }}
              >
                ←
              </button>
              {(() => {
                const totalPages = historyTotalPages || 1;
                const currentPage = historyPage;
                let startPage = Math.max(0, currentPage - 1);
                let endPage = Math.min(totalPages - 1, startPage + 2);
                if (endPage - startPage < 2) {
                  startPage = Math.max(0, endPage - 2);
                }
                const pages = [];
                for (let i = startPage; i <= endPage; i++) {
                  pages.push(
                    <button
                      key={i}
                      onClick={() => setHistoryPage(i)}
                      style={{
                        padding: '3px 6px',
                        backgroundColor: i === currentPage ? '#ffa500' : '#003366',
                        color: 'white',
                        border: 'none',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        fontSize: '11px',
                        fontWeight: i === currentPage ? 'bold' : '600',
                        minWidth: '24px'
                      }}
                    >
                      {i + 1}
                    </button>
                  );
                }
                return pages;
              })()}
              <button
                onClick={() => setHistoryPage(historyPage + 1)}
                disabled={historyPage >= historyTotalPages - 1}
                style={{
                  padding: '3px 6px',
                  backgroundColor: historyPage >= historyTotalPages - 1 ? '#ccc' : '#003366',
                  color: 'white',
                  border: 'none',
                  borderRadius: '3px',
                  cursor: historyPage >= historyTotalPages - 1 ? 'not-allowed' : 'pointer',
                  fontSize: '11px',
                  fontWeight: '600',
                  minWidth: '24px'
                }}
              >
                →
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'devices' && (
        <div className="tab-content">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', gap: '8px' }}>
            <h2 style={{ margin: 0 }}>All RFID Devices</h2>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'nowrap' }}>
              <button onClick={unassignSelectedDevices} disabled={isLoadingDevices} style={{
                padding: '4px 8px',
                fontSize: '11px',
                color: 'white',
                backgroundColor: isLoadingDevices ? '#ccc' : '#003366',
                border: 'none',
                borderRadius: '3px',
                cursor: isLoadingDevices ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                whiteSpace: 'nowrap'
              }}>Unassign</button>
              <button onClick={exportToExcel} disabled={isLoadingDevices || devices.length === 0} style={{
                padding: '4px 8px',
                fontSize: '11px',
                color: 'white',
                backgroundColor: (isLoadingDevices || devices.length === 0) ? '#ccc' : '#003366',
                border: 'none',
                borderRadius: '3px',
                cursor: (isLoadingDevices || devices.length === 0) ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                whiteSpace: 'nowrap'
              }}>Export</button>
              <button onClick={() => setShowRackReassignmentModal(true)} disabled={isLoadingDevices || devices.length === 0} style={{
                padding: '4px 8px',
                fontSize: '11px',
                color: 'white',
                backgroundColor: (isLoadingDevices || devices.length === 0) ? '#ccc' : '#003366',
                border: 'none',
                borderRadius: '3px',
                cursor: (isLoadingDevices || devices.length === 0) ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                whiteSpace: 'nowrap'
              }}>Reassign</button>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center', marginBottom: '15px', flexWrap: 'nowrap' }}>
            <input
              type="text"
              placeholder="Search..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              disabled={isLoadingDevices}
              className="search-input-field"
              style={{
                flex: '0 1 auto',
                width: '150px',
                padding: '4px 8px',
                border: '2px solid #333',
                borderRadius: '3px',
                fontSize: '11px',
                color: '#000',
                backgroundColor: '#fff'
              }}
            />
            <button
              onClick={loadAllDevices}
              disabled={isLoadingDevices}
              style={{
                padding: '4px 8px',
                fontSize: '11px',
                whiteSpace: 'nowrap',
                color: 'white',
                backgroundColor: '#003366',
                border: 'none',
                borderRadius: '3px',
                cursor: isLoadingDevices ? 'not-allowed' : 'pointer',
                fontWeight: '600'
              }}
            >
              {isLoadingDevices ? 'Loading...' : 'Refresh'}
            </button>
            {devices.length > 0 && (
              <div style={{
                padding: '4px 8px',
                backgroundColor: '#f0f8ff',
                border: '2px solid #003366',
                borderRadius: '3px',
                fontSize: '11px',
                fontWeight: '600',
                color: '#003366',
                whiteSpace: 'nowrap',
                marginLeft: 'auto'
              }}>
                Total Live Tags: {getSortedDevices().length}{filterText ? ` (filtered from ${devices.length})` : ''}
              </div>
            )}
          </div>
          {isLoadingDevices ? (
            <div className="loading-spinner">
              <p>Loading devices, please wait...</p>
              <div className="spinner"></div>
            </div>
          ) : devices.length === 0 ? (
            <p>No devices loaded. Click "Load All Devices" to fetch data.</p>
          ) : (
            <div className="devices-table-wrapper">
              {Object.keys(pendingUpdates).length > 0 && (
                <button
                  onClick={() => setShowUpdateModal(true)}
                  style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    padding: '10px 20px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    zIndex: 10
                  }}
                >
                  Update ({Object.keys(pendingUpdates).length})
                </button>
              )}
              <table className="devices-table">
                <thead>
                  <tr>
                    <th>Select</th>
                    {devicesColumns.map(col => (
                      <th key={col} onClick={() => handleSort(col)} style={{cursor: 'pointer'}}>
                        {col}
                        {sortConfig.key === col && (
                          <span>{sortConfig.direction === 'asc' ? ' ▲' : ' ▼'}</span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {getSortedDevices().map((device, rowIndex) => (
                  <tr key={device.readerCode || rowIndex}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedDevices.includes(device.readerCode)}
                        onChange={() => toggleDeviceSelection(device.readerCode)}
                      />
                    </td>
                    {devicesColumns.map(col => (
                      <td
                        key={col}
                        onDoubleClick={() => handleCellDoubleClick(device, col)}
                      >
                        {editingCell?.device?.readerCode === device.readerCode && editingCell?.colName === col ? (
                          <input
                            type="text"
                            defaultValue={device[col] || ''}
                            autoFocus
                            onBlur={e => handleCellEdit(device, col, e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') handleCellEdit(device, col, e.target.value);
                              if (e.key === 'Escape') setEditingCell(null);
                            }}
                          />
                        ) : (
                          typeof device[col] === 'object' && device[col] !== null
                            ? (device[col].name || JSON.stringify(device[col]))
                            : (device[col] || '')
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>
      )}

      {activeTab === 'scan' && (
        <div className="tab-content">
          <h2 style={{ marginBottom: '20px', color: '#003366' }}>Single Device Scan</h2>

          <div className="section">
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', fontSize: '14px' }}>
                Scan or Enter Device ID:
              </label>
              <input
                ref={singleScanInputRef}
                type="text"
                value={scannedDeviceId}
                onChange={(e) => setScannedDeviceId(e.target.value.toUpperCase())}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    loadScannedDevice();
                  }
                }}
                placeholder="Enter Device ID (e.g., FF00D8E20BDF)"
                autoFocus
                style={{
                  width: '100%',
                  padding: '10px',
                  fontFamily: 'monospace',
                  fontSize: '16px',
                  textTransform: 'uppercase',
                  border: '2px solid #003366',
                  borderRadius: '4px',
                  marginBottom: '10px'
                }}
              />
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={loadScannedDevice}
                  disabled={isLoadingScannedDevice || !scannedDeviceId.trim()}
                  style={{
                    flex: 1,
                    padding: '12px',
                    fontSize: '16px',
                    backgroundColor: (isLoadingScannedDevice || !scannedDeviceId.trim()) ? '#ccc' : '#003366',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: (isLoadingScannedDevice || !scannedDeviceId.trim()) ? 'not-allowed' : 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  {isLoadingScannedDevice ? 'Loading...' : 'Search Device'}
                </button>
                <button
                  onClick={() => {
                    setScannedDeviceId('');
                    setScannedDeviceData(null);
                    setPendingScannedChange(null);
                    setEditingScannedField(null);
                    // Focus the input after clearing
                    setTimeout(() => {
                      if (singleScanInputRef.current) {
                        singleScanInputRef.current.focus();
                      }
                    }, 0);
                  }}
                  disabled={isLoadingScannedDevice}
                  style={{
                    padding: '12px 24px',
                    fontSize: '16px',
                    backgroundColor: isLoadingScannedDevice ? '#ccc' : '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isLoadingScannedDevice ? 'not-allowed' : 'pointer',
                    fontWeight: 'bold',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>

          {isLoadingScannedDevice && (
            <div className="loading-spinner">
              <p>Loading device details...</p>
              <div className="spinner"></div>
            </div>
          )}

          {scannedDeviceData && !isLoadingScannedDevice && (
            <div className="section">
              <h3 style={{ marginBottom: '15px', color: '#003366' }}>Device Details</h3>

              <div style={{
                backgroundColor: '#f0f8ff',
                border: '2px solid #003366',
                borderRadius: '6px',
                padding: '20px'
              }}>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px', fontSize: '13px', color: '#555' }}>
                    Device ID:
                  </label>
                  <div style={{
                    padding: '8px',
                    backgroundColor: '#e8f4ff',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}>
                    {scannedDeviceData.reader_code || scannedDeviceData.readerCode || 'N/A'}
                  </div>
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px', fontSize: '13px', color: '#555' }}>
                    Rack ID: <span style={{ fontWeight: 'normal', fontStyle: 'italic', fontSize: '12px' }}>(double-click to edit)</span>
                  </label>
                  {editingScannedField === 'shelf_id' ? (
                    <input
                      type="text"
                      defaultValue={scannedDeviceData.shelf_id || scannedDeviceData.shelfId || ''}
                      autoFocus
                      onBlur={(e) => {
                        handleScannedFieldChange('shelf_id', e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleScannedFieldChange('shelf_id', e.target.value);
                        }
                        if (e.key === 'Escape') {
                          setEditingScannedField(null);
                          setPendingScannedChange(null);
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '2px solid #003366',
                        borderRadius: '4px',
                        fontSize: '16px',
                        fontWeight: 'bold'
                      }}
                    />
                  ) : (
                    <div
                      onDoubleClick={() => setEditingScannedField('shelf_id')}
                      style={{
                        padding: '8px',
                        backgroundColor: 'white',
                        border: '2px solid #28a745',
                        borderRadius: '4px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        minHeight: '38px',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      {scannedDeviceData.shelf_id || scannedDeviceData.shelfId || '(not assigned)'}
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px', fontSize: '13px', color: '#555' }}>
                    Index: <span style={{ fontWeight: 'normal', fontStyle: 'italic', fontSize: '12px' }}>(double-click to edit)</span>
                  </label>
                  {editingScannedField === 'index_order' ? (
                    <input
                      type="number"
                      defaultValue={scannedDeviceData.index_order || scannedDeviceData.index || ''}
                      autoFocus
                      onBlur={(e) => {
                        handleScannedFieldChange('index_order', e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleScannedFieldChange('index_order', e.target.value);
                        }
                        if (e.key === 'Escape') {
                          setEditingScannedField(null);
                          setPendingScannedChange(null);
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '2px solid #003366',
                        borderRadius: '4px',
                        fontSize: '16px',
                        fontWeight: 'bold'
                      }}
                    />
                  ) : (
                    <div
                      onDoubleClick={() => setEditingScannedField('index_order')}
                      style={{
                        padding: '8px',
                        backgroundColor: 'white',
                        border: '2px solid #28a745',
                        borderRadius: '4px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        minHeight: '38px',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      {scannedDeviceData.index_order || scannedDeviceData.index || 'N/A'}
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: '0' }}>
                  <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px', fontSize: '13px', color: '#555' }}>
                    Group: <span style={{ fontWeight: 'normal', fontStyle: 'italic', fontSize: '12px' }}>(double-click to edit)</span>
                  </label>
                  {editingScannedField === 'group_name' ? (
                    <input
                      type="text"
                      defaultValue={scannedDeviceData.group_name || scannedDeviceData.groupName || ''}
                      autoFocus
                      onBlur={(e) => {
                        handleScannedFieldChange('group_name', e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleScannedFieldChange('group_name', e.target.value);
                        }
                        if (e.key === 'Escape') {
                          setEditingScannedField(null);
                          setPendingScannedChange(null);
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '2px solid #003366',
                        borderRadius: '4px',
                        fontSize: '16px',
                        fontWeight: 'bold'
                      }}
                    />
                  ) : (
                    <div
                      onDoubleClick={() => setEditingScannedField('group_name')}
                      style={{
                        padding: '8px',
                        backgroundColor: 'white',
                        border: '2px solid #28a745',
                        borderRadius: '4px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        minHeight: '38px',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      {scannedDeviceData.group_name || scannedDeviceData.groupName || '(not set)'}
                    </div>
                  )}
                </div>
              </div>

              {pendingScannedChange && (
                <button
                  onClick={saveScannedDeviceChange}
                  style={{
                    width: '100%',
                    marginTop: '15px',
                    padding: '12px',
                    fontSize: '16px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  Save Changes
                </button>
              )}

              <button
                onClick={unassignScannedDevice}
                style={{
                  width: '100%',
                  marginTop: '15px',
                  padding: '12px',
                  fontSize: '16px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Unassign Device
              </button>

              <div style={{
                marginTop: '20px',
                padding: '15px',
                backgroundColor: '#fff9e6',
                border: '1px solid #ffc107',
                borderRadius: '4px',
                fontSize: '13px'
              }}>
                <p style={{ margin: 0, fontWeight: 'bold', color: '#856404' }}>
                  📝 Note: Double-click on Rack ID, Index, or Group to edit. Changes will show a Save button.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Ground Truth Tab */}
      {activeTab === 'groundtruth' && (
        <div className="tab-content">
          <h2 style={{ marginBottom: '20px', color: '#003366' }}>Ground Truth Verification</h2>

          <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
            <button
              onClick={() => setGtView('new')}
              style={{
                padding: '8px 16px',
                backgroundColor: gtView === 'new' ? '#003366' : '#e0e0e0',
                color: gtView === 'new' ? 'white' : '#333',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              New Scan
            </button>
            <button
              onClick={() => setGtView('history')}
              style={{
                padding: '8px 16px',
                backgroundColor: gtView === 'history' ? '#003366' : '#e0e0e0',
                color: gtView === 'history' ? 'white' : '#333',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              History
            </button>
          </div>

          {gtView === 'new' && !gtCurrentSession && (
            <div className="section">
              <h3>Start New Ground Truth Session</h3>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  Rack ID:
                </label>
                <input
                  type="text"
                  value={gtRackId}
                  onChange={(e) => setGtRackId(e.target.value.toUpperCase())}
                  placeholder="Enter Rack ID (e.g., C1-2)"
                  style={{
                    width: '200px',
                    padding: '8px',
                    border: '2px solid #003366',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <button
                onClick={async () => {
                  if (!gtRackId.trim()) {
                    alert('Please enter a Rack ID');
                    return;
                  }
                  setGtIsLoading(true);
                  try {
                    const response = await axiosAuth.post('/api/groundtruth/sessions', {
                      rackId: gtRackId.trim(),
                      scannedBy: 'User', // TODO: Get from auth
                      company,
                      store
                    });
                    setGtCurrentSession(response.data);
                    alert(`Session created: ${response.data.sessionId}`);
                  } catch (error) {
                    alert(`Error creating session: ${error.message}`);
                  } finally {
                    setGtIsLoading(false);
                  }
                }}
                disabled={gtIsLoading || !gtRackId.trim()}
                style={{
                  padding: '12px 24px',
                  backgroundColor: (!gtRackId.trim() || gtIsLoading) ? '#ccc' : '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: (!gtRackId.trim() || gtIsLoading) ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold',
                  fontSize: '16px'
                }}
              >
                {gtIsLoading ? 'Creating...' : 'Start Session'}
              </button>
            </div>
          )}

          {gtView === 'new' && gtCurrentSession && !gtComparisonResult && (
            <div>
              <div className="section">
                <h3>Session: {gtCurrentSession.sessionId}</h3>
                <p><strong>Rack:</strong> {gtCurrentSession.rackId}</p>
                <p><strong>Created:</strong> {new Date(gtCurrentSession.createdAt).toLocaleString()}</p>
              </div>

              {/* Step 1: Zebra Scan (Placeholder) */}
              <div className="section" style={{ backgroundColor: '#f0f8ff' }}>
                <h3>Step 1: Zebra RFID Scan</h3>
                <p style={{ marginBottom: '10px' }}>
                  Zebra Scanner Integration - Coming Soon
                </p>
                <p style={{ fontSize: '12px', color: '#666', marginBottom: '15px' }}>
                  For now, you can manually enter EPCs or skip to manual UPC scanning.
                </p>
                {gtZebraResults.length > 0 && (
                  <div style={{ marginTop: '15px' }}>
                    <strong>Zebra Results: {gtZebraResults.length} EPCs</strong>
                  </div>
                )}
              </div>

              {/* Step 2: Manual UPC Scan */}
              <div className="section" style={{ backgroundColor: '#f0fff0' }}>
                <h3>Step 2: Manual UPC Barcode Scan</h3>
                <p style={{ marginBottom: '10px' }}>
                  Scan or enter UPC barcodes (12 digits):
                </p>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                  <input
                    type="text"
                    value={gtManualInput}
                    onChange={(e) => setGtManualInput(e.target.value.replace(/\D/g, ''))}
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter' && gtManualInput.length === 12) {
                        try {
                          await axiosAuth.post(`/api/groundtruth/sessions/${gtCurrentSession.sessionId}/manual`, {
                            upc: gtManualInput
                          });
                          setGtManualUpcs([...gtManualUpcs, gtManualInput]);
                          setGtManualInput('');
                        } catch (error) {
                          alert(`Error adding UPC: ${error.message}`);
                        }
                      }
                    }}
                    placeholder="Enter 12-digit UPC"
                    maxLength={12}
                    style={{
                      flex: 1,
                      padding: '8px',
                      border: '2px solid #28a745',
                      borderRadius: '4px',
                      fontSize: '14px',
                      fontFamily: 'monospace'
                    }}
                    autoFocus
                  />
                  <button
                    onClick={async () => {
                      if (gtManualInput.length !== 12) {
                        alert('UPC must be 12 digits');
                        return;
                      }
                      try {
                        await axiosAuth.post(`/api/groundtruth/sessions/${gtCurrentSession.sessionId}/manual`, {
                          upc: gtManualInput
                        });
                        setGtManualUpcs([...gtManualUpcs, gtManualInput]);
                        setGtManualInput('');
                      } catch (error) {
                        alert(`Error adding UPC: ${error.message}`);
                      }
                    }}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    Add
                  </button>
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <strong>Scanned: {gtManualUpcs.length} UPCs</strong>
                  {gtManualUpcs.length > 0 && (
                    <button
                      onClick={async () => {
                        try {
                          await axiosAuth.delete(`/api/groundtruth/sessions/${gtCurrentSession.sessionId}/manual/last`);
                          setGtManualUpcs(gtManualUpcs.slice(0, -1));
                        } catch (error) {
                          alert(`Error removing UPC: ${error.message}`);
                        }
                      }}
                      style={{
                        marginLeft: '10px',
                        padding: '4px 12px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Undo Last
                    </button>
                  )}
                </div>

                {gtManualUpcs.length > 0 && (
                  <div style={{
                    maxHeight: '200px',
                    overflow: 'auto',
                    backgroundColor: 'white',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    padding: '10px',
                    fontFamily: 'monospace',
                    fontSize: '12px'
                  }}>
                    {gtManualUpcs.map((upc, idx) => (
                      <div key={idx}>{idx + 1}. {upc}</div>
                    ))}
                  </div>
                )}
              </div>

              {/* Step 3: Compare */}
              {gtManualUpcs.length > 0 && (
                <div className="section">
                  <button
                    onClick={async () => {
                      setGtIsLoading(true);
                      try {
                        const response = await axiosAuth.post(`/api/groundtruth/sessions/${gtCurrentSession.sessionId}/compare`);
                        setGtComparisonResult(response.data);
                      } catch (error) {
                        alert(`Error comparing: ${error.message}`);
                      } finally {
                        setGtIsLoading(false);
                      }
                    }}
                    disabled={gtIsLoading}
                    style={{
                      width: '100%',
                      padding: '15px',
                      backgroundColor: gtIsLoading ? '#ccc' : '#003366',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: gtIsLoading ? 'not-allowed' : 'pointer',
                      fontWeight: 'bold',
                      fontSize: '16px'
                    }}
                  >
                    {gtIsLoading ? 'Comparing...' : 'Compare Results'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Comparison Results */}
          {gtView === 'new' && gtComparisonResult && (
            <div>
              <div className="section" style={{ backgroundColor: '#e8f5e9' }}>
                <h3>Comparison Results</h3>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2e7d32', marginBottom: '20px' }}>
                  Accuracy: {gtComparisonResult.accuracyPercent}%
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                  <div style={{ padding: '15px', backgroundColor: 'white', borderRadius: '4px', border: '2px solid #28a745' }}>
                    <div style={{ fontSize: '12px', color: '#666' }}>Matched</div>
                    <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#28a745' }}>{gtComparisonResult.matched}</div>
                  </div>
                  <div style={{ padding: '15px', backgroundColor: 'white', borderRadius: '4px', border: '2px solid #ffc107' }}>
                    <div style={{ fontSize: '12px', color: '#666' }}>Manual Scans</div>
                    <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#ffc107' }}>{gtComparisonResult.totalManualScans}</div>
                  </div>
                  <div style={{ padding: '15px', backgroundColor: 'white', borderRadius: '4px', border: '2px solid #dc3545' }}>
                    <div style={{ fontSize: '12px', color: '#666' }}>Missing in Zebra</div>
                    <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#dc3545' }}>{gtComparisonResult.missingInZebra.length}</div>
                  </div>
                  <div style={{ padding: '15px', backgroundColor: 'white', borderRadius: '4px', border: '2px solid #17a2b8' }}>
                    <div style={{ fontSize: '12px', color: '#666' }}>Extra in Zebra</div>
                    <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#17a2b8' }}>{gtComparisonResult.extraInZebra.length}</div>
                  </div>
                </div>

                {gtComparisonResult.missingInZebra.length > 0 && (
                  <div style={{ marginTop: '20px' }}>
                    <h4 style={{ color: '#dc3545' }}>❌ Missing in Zebra Scan:</h4>
                    <div style={{ fontFamily: 'monospace', fontSize: '12px', backgroundColor: 'white', padding: '10px', borderRadius: '4px' }}>
                      {gtComparisonResult.missingInZebra.map((upc, idx) => (
                        <div key={idx}>{upc}</div>
                      ))}
                    </div>
                  </div>
                )}

                {gtComparisonResult.extraInZebra.length > 0 && (
                  <div style={{ marginTop: '20px' }}>
                    <h4 style={{ color: '#17a2b8' }}>⚠️ Extra in Zebra Scan (not in manual):</h4>
                    <div style={{ fontFamily: 'monospace', fontSize: '12px', backgroundColor: 'white', padding: '10px', borderRadius: '4px' }}>
                      {gtComparisonResult.extraInZebra.map((item, idx) => (
                        <div key={idx}>{item.upc} (EPC: {item.epc})</div>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  onClick={() => {
                    setGtCurrentSession(null);
                    setGtComparisonResult(null);
                    setGtManualUpcs([]);
                    setGtZebraResults([]);
                    setGtRackId('');
                  }}
                  style={{
                    marginTop: '20px',
                    padding: '12px 24px',
                    backgroundColor: '#003366',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  Start New Session
                </button>
              </div>
            </div>
          )}

          {/* History View */}
          {gtView === 'history' && (
            <div className="section">
              <h3>Ground Truth History</h3>
              <p style={{ color: '#666' }}>History view coming soon...</p>
              <button
                onClick={async () => {
                  setGtIsLoading(true);
                  try {
                    const response = await axiosAuth.get('/api/groundtruth/sessions');
                    setGtSessions(response.data.sessions || []);
                  } catch (error) {
                    alert(`Error loading history: ${error.message}`);
                  } finally {
                    setGtIsLoading(false);
                  }
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#003366',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Load History
              </button>

              {gtSessions.length > 0 && (
                <div style={{ marginTop: '20px' }}>
                  <table className="history-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Rack ID</th>
                        <th>User</th>
                        <th>Accuracy</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gtSessions.map((session, idx) => (
                        <tr key={idx}>
                          <td>{new Date(session.createdAt).toLocaleString()}</td>
                          <td>{session.rackId}</td>
                          <td>{session.scannedBy}</td>
                          <td>{session.accuracyPercent}%</td>
                          <td>{session.comparisonStatus}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Rack Reassignment Modal */}
      {showRackReassignmentModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            width: '90%'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, color: '#003366' }}>Rack Re-assignment</h2>
              <button
                onClick={() => {
                  setShowRackReassignmentModal(false);
                  setSearchQuery('');
                  setFoundDevices([]);
                  setNewRackId('');
                  setNewGroup('A');
                  setReassignmentLog([]);
                }}
                style={{
                  padding: '5px 10px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}
              >
                ✕
              </button>
            </div>

            <p style={{ marginBottom: '20px', color: '#666' }}>
              Search by Device ID or Rack ID to find devices, then reassign them all to a new rack.
            </p>

            <div style={{
              backgroundColor: '#e7f3ff',
              border: '2px solid #2196F3',
              borderRadius: '6px',
              padding: '15px',
              marginBottom: '20px'
            }}>
              <p style={{ margin: '0 0 10px 0', fontWeight: 'bold', color: '#1976D2' }}>
                💡 Search by:
              </p>
              <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px' }}>
                <li><strong>Device ID</strong> - finds all devices on the same rack as that device</li>
                <li><strong>Rack ID</strong> - finds all devices currently on that rack</li>
              </ul>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Device ID or Rack ID:</label>
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value.toUpperCase())}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    searchDevices();
                  }
                }}
                placeholder="Enter Device ID (e.g., 01234567890ABC) or Rack ID (e.g., R-100)"
                style={{
                  width: '100%',
                  padding: '10px',
                  fontFamily: 'monospace',
                  fontSize: '16px',
                  textTransform: 'uppercase',
                  border: '2px solid #333',
                  borderRadius: '4px'
                }}
              />
              <button
                type="button"
                onClick={searchDevices}
                disabled={isSearching || !searchQuery.trim()}
                style={{
                  marginTop: '10px',
                  padding: '10px 20px',
                  fontSize: '16px',
                  backgroundColor: (isSearching) ? '#cccccc' : '#003366',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: (isSearching) ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  width: '100%'
                }}
              >
                {isSearching ? 'Searching...' : 'Search Devices'}
              </button>
            </div>

            {foundDevices.length > 0 && (
              <>
                <div style={{
                  backgroundColor: '#f0f8ff',
                  border: '2px solid #003366',
                  borderRadius: '6px',
                  padding: '15px',
                  marginBottom: '20px'
                }}>
                  <p style={{ margin: 0 }}><strong>Found:</strong> {foundDevices.length} device(s)</p>
                  <p style={{ margin: '5px 0 0 0' }}><strong>Current Rack ID:</strong> {foundDevices[0]?.shelfId || 'Not assigned'}</p>
                </div>

                <div style={{
                  maxHeight: '200px',
                  overflowY: 'auto',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '10px',
                  backgroundColor: '#fafafa',
                  marginBottom: '20px'
                }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #ddd', backgroundColor: '#003366', color: 'white' }}>
                        <th style={{ padding: '8px', textAlign: 'left' }}>Device ID</th>
                        <th style={{ padding: '8px', textAlign: 'center' }}>Index</th>
                        <th style={{ padding: '8px', textAlign: 'center' }}>Group</th>
                      </tr>
                    </thead>
                    <tbody>
                      {foundDevices.map((device, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '8px', fontFamily: 'monospace' }}>{device.readerCode}</td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>{device.index}</td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>{device.groupName || 'N/A'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                    New Rack ID <span style={{ fontWeight: 'normal', color: '#666', fontSize: '13px' }}>(optional - leave empty to keep current rack)</span>:
                  </label>
                  <input
                    type="text"
                    value={newRackId}
                    onChange={e => setNewRackId(e.target.value)}
                    placeholder="Enter new rack ID (or leave empty to keep current)"
                    style={{
                      width: '100%',
                      padding: '10px',
                      fontSize: '16px',
                      border: '2px solid #333',
                      borderRadius: '4px'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Group:</label>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', margin: 0 }}>
                      <input
                        type="radio"
                        name="newGroup"
                        value="A"
                        checked={newGroup === 'A'}
                        onChange={e => setNewGroup(e.target.value)}
                        style={{ cursor: 'pointer' }}
                      />
                      <span>Group A</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', margin: 0 }}>
                      <input
                        type="radio"
                        name="newGroup"
                        value="B"
                        checked={newGroup === 'B'}
                        onChange={e => setNewGroup(e.target.value)}
                        style={{ cursor: 'pointer' }}
                      />
                      <span>Group B</span>
                    </label>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={reassignRack}
                  disabled={foundDevices.length === 0}
                  style={{
                    padding: '12px 24px',
                    fontSize: '16px',
                    backgroundColor: foundDevices.length === 0 ? '#cccccc' : '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: foundDevices.length === 0 ? 'not-allowed' : 'pointer',
                    fontWeight: 'bold',
                    width: '100%'
                  }}
                >
                  {newRackId.trim() ? `Reassign ${foundDevices.length} Device(s)` : `Update Group for ${foundDevices.length} Device(s)`}
                </button>
              </>
            )}

            {reassignmentLog.length > 0 && (
              <div style={{
                marginTop: '20px',
                padding: '15px',
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                maxHeight: '200px',
                overflowY: 'auto'
              }}>
                <h4 style={{ margin: '0 0 10px 0' }}>Status Log:</h4>
                <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                  {reassignmentLog.map((log, idx) => (
                    <div key={idx}>{log}</div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Conflict Resolution Modal */}
      {conflictModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            width: '90%'
          }}>
            <h2 style={{ marginTop: 0, color: '#dc3545' }}>⚠️ Conflict Detected</h2>
            <p style={{ marginBottom: '20px' }}>
              The following devices are already assigned:
            </p>

            <div style={{
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '4px',
              padding: '15px',
              marginBottom: '20px',
              maxHeight: '200px',
              overflowY: 'auto'
            }}>
              {conflictModal.alreadyAssigned.map((device, idx) => (
                <div key={idx} style={{ marginBottom: '8px', fontFamily: 'monospace', fontSize: '13px' }}>
                  • <strong>{device.deviceId}</strong> → Rack "{device.rackId}" (Index: {device.index})
                </div>
              ))}
            </div>

            <p style={{ marginBottom: '20px', fontWeight: 'bold' }}>
              Choose how to proceed:
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button
                onClick={() => handleConflictResolution('clear_rack')}
                style={{
                  padding: '12px 20px',
                  backgroundColor: '#ff6b6b',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  textAlign: 'left'
                }}
              >
                Unassign All Previous Readers Associated with Rack
                <div style={{ fontSize: '12px', fontWeight: 'normal', marginTop: '5px', opacity: 0.9 }}>
                  Clear ALL devices from rack "{conflictModal.targetRackId}" and assign new devices starting at index 1
                </div>
              </button>

              <button
                onClick={() => handleConflictResolution('reassign')}
                style={{
                  padding: '12px 20px',
                  backgroundColor: '#ffc107',
                  color: '#000',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  textAlign: 'left'
                }}
              >
                Continue from Last Index
                <div style={{ fontSize: '12px', fontWeight: 'normal', marginTop: '5px', opacity: 0.8 }}>
                  Unassign only the {conflictModal.alreadyAssigned.length} conflicting device(s) and continue adding from the last index on rack "{conflictModal.targetRackId}"
                </div>
              </button>

              <button
                onClick={() => handleConflictResolution('cancel')}
                style={{
                  padding: '12px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Leaderboard Modal */}
      {showLeaderboard && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '700px',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{ marginTop: 0, color: '#003366' }}>🏆 Assignment Leaderboard</h2>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #ddd', backgroundColor: '#003366', color: 'white' }}>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Rank</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>User</th>
                  <th style={{ padding: '10px', textAlign: 'center' }}>Total</th>
                  <th style={{ padding: '10px', textAlign: 'center' }}>Assigns</th>
                  <th style={{ padding: '10px', textAlign: 'center' }}>Unassigns</th>
                  <th style={{ padding: '10px', textAlign: 'center' }}>Group Changes</th>
                </tr>
              </thead>
              <tbody>
                {leaderboardData.map((entry, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #eee', backgroundColor: idx === 0 ? '#fff9e6' : 'transparent' }}>
                    <td style={{ padding: '10px', fontWeight: 'bold' }}>
                      {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : idx + 1}
                    </td>
                    <td style={{ padding: '10px', fontWeight: idx < 3 ? 'bold' : 'normal' }}>{entry.user}</td>
                    <td style={{ padding: '10px', textAlign: 'center', fontWeight: 'bold' }}>{entry.totalAssignments}</td>
                    <td style={{ padding: '10px', textAlign: 'center', color: '#28a745' }}>{entry.assigns}</td>
                    <td style={{ padding: '10px', textAlign: 'center', color: '#dc3545' }}>{entry.unassigns}</td>
                    <td style={{ padding: '10px', textAlign: 'center', color: '#ffc107' }}>{entry.groupChanges}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowLeaderboard(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#003366',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update Confirmation Modal */}
      {showUpdateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '600px',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{ marginTop: 0 }}>Confirm Group Name Updates</h2>
            <p>You are about to update the following devices:</p>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #ddd' }}>
                  <th style={{ padding: '8px', textAlign: 'left' }}>Device ID</th>
                  <th style={{ padding: '8px', textAlign: 'left' }}>Old Group</th>
                  <th style={{ padding: '8px', textAlign: 'left' }}>New Group</th>
                </tr>
              </thead>
              <tbody>
                {Object.values(pendingUpdates).map((update, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '8px' }}>{update.device.readerCode}</td>
                    <td style={{ padding: '8px' }}>{update.oldValue || '(empty)'}</td>
                    <td style={{ padding: '8px', fontWeight: 'bold' }}>{update.newValue}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowUpdateModal(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleBatchUpdate}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </ProtectedComponent>
  );
};

export default RFIDBatchAssign;
